import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import {
  CssBaseline,
  List,
  Divider,
  IconButton,
  Paper,
  Typography,
  Toolbar,
  Link,
  Box,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MuiAppBar from '@mui/material/AppBar';
import NotificationsMenu from './NotificationsMenu';
import { mainListItems } from './listItems';
import Footer from '../components/Footer';
import { MainListItems } from './listItems';

const drawerWidth = 280;

// Styled Components
const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(8px)',
  boxShadow: 'none',
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  color: theme.palette.text.primary,
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.up('md')]: {
    ...(open && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  },
}));

const DrawerContent = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(8px)',
  borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

// FIXED: shouldForwardProp prevents isOpen from being passed to the DOM element
const LogoContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})(({ theme, isOpen }) => ({
  padding: isOpen ? theme.spacing(2) : 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
  transition: 'padding 0.3s ease',
}));

// FIXED: shouldForwardProp prevents isOpen from being passed to the DOM element
const LogoText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})(({ theme, isOpen }) => ({
  fontWeight: 700,
  fontSize: '1.5rem',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
  marginRight: theme.spacing(2),
  transition: 'opacity 0.3s ease, margin 0.3s ease',
  opacity: isOpen ? 1 : 0,
  marginLeft: isOpen ? theme.spacing(2) : 0,
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
  transition: 'all 0.3s ease',
}));

export default function DashTheme() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const pathName = location.pathname.split('/').pop();
  const [open, setOpen] = useState(!isMobile);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setOpen(!open);
    }
  };

  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  const drawer = (
    <DrawerContent>
      <LogoContainer isOpen={open}>
        <LogoText variant="h6" isOpen={open}>Gold Petals</LogoText>
        {!isMobile && (
          <StyledIconButton onClick={handleDrawerToggle}>
            <ChevronLeftIcon />
          </StyledIconButton>
        )}
      </LogoContainer>
      <Divider sx={{ opacity: 0.1 }} />
      <List component="nav" sx={{ 
        paddingTop: 1,
        paddingX: 1,
        '& > *': {
          marginBottom: 0.5,
        }
      }}>
        <MainListItems open={open} />
      </List>
      {open && <Footer />}
    </DrawerContent>
  );

  return (
    <>
      <CssBaseline />
      <StyledAppBar position="fixed" open={open && !isMobile}>
        <Toolbar sx={{ pr: '24px' }}>
          <StyledIconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            sx={{
              marginRight: '36px',
              ...(!isMobile && open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </StyledIconButton>
          <PageTitle
            component="h1"
            variant="h6"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {pathName.charAt(0).toUpperCase() + pathName.slice(1)}
          </PageTitle>
          <NotificationsMenu />
        </Toolbar>
      </StyledAppBar>

      {/* Desktop Drawer */}
      {!isMobile && (
        <Box
          component="nav"
          sx={{
            width: { md: open ? drawerWidth : theme.spacing(9) },
            flexShrink: 0,
          }}
        >
          <SwipeableDrawer
            variant="permanent"
            open={open}
            onClose={() => {}}
            onOpen={() => {}}
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                width: open ? drawerWidth : theme.spacing(10),
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                overflowX: 'hidden',
              },
            }}
          >
            {drawer}
          </SwipeableDrawer>
          
        </Box>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <SwipeableDrawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          onOpen={() => setMobileOpen(true)}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </SwipeableDrawer>
      )}
    </>
  );
}